import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { fetchDepartmentTasks, fetchTeamMembers, fetchResourceStatus } from './operationsSlice'
import { IDepartmentBoardTask, TaskSource, PRIORITY_COLORS, TaskStatus } from './types/operations.types'
import { OperationsTabBar } from './components/OperationsTabBar'
import { SourceBadge } from './components/SourceBadge'
import { PriorityBadge } from './components/PriorityBadge'
import { TeamPanel } from './components/TeamPanel'
import {
  MOCK_MACHINES_DOWN,
  MOCK_PARTS_AT_RISK,
  MOCK_LOCATION_STATUS,
  MOCK_POST_COVERAGE,
} from './mockData/departments.mock'
import { MOCK_PLANS } from './mockData/plans.mock'
import {
  OpsPageWrapper,
  OpsCard,
  OpsCardHeader,
  OpsGrid,
  DeptSelector,
  DeptButton,
  FilterPills,
  FilterPill,
  TaskCard,
  TaskCardHeader,
  TaskCardTitle,
  TaskCardSubtitle,
  TaskCardBadges,
  TaskCardMeta,
  Badge,
  WarningText,
  DangerText,
  OpsButton,
  ProgressBarWrapper,
  ProgressBarFill,
  StatusDot,
  opsColors,
} from './styled/Operations.styled'
import { NavBar } from 'components/NavBar/NavBar'

const DEPARTMENTS = [
  { key: 'maintenance', name: 'Maintenance', icon: '🔧', color: opsColors.warningBase },
  { key: 'camp-services', name: 'Camp Services', icon: '🧹', color: opsColors.infoBase },
  { key: 'security', name: 'Security', icon: '🛡️', color: opsColors.dangerBase },
  { key: 'mining-operations', name: 'Mining Operations', icon: '⛏️', color: opsColors.brand500 },
]

const STATUS_FILTERS = ['All', 'In Progress', 'Queued', 'Waiting', 'Blocked', 'Completed', 'On Post']
const SOURCE_FILTERS: Array<{ label: string; value: string }> = [
  { label: 'All', value: 'all' },
  { label: 'Operation Plan', value: 'PLAN' },
  { label: 'Shift Program', value: 'SHIFT_PROGRAM' },
  { label: 'Trigger', value: 'TRIGGER' },
  { label: 'Problem Resolver', value: 'PROBLEM_RESOLVER' },
  { label: 'Manual', value: 'MANUAL' },
]

const statusLabelToFilter = (statusLabel: string) => {
  const map: Record<string, string[]> = {
    'In Progress': ['In Progress', 'Loading', 'Returning'],
    Queued: ['Queued'],
    Blocked: ['Blocked'],
    Completed: ['Completed'],
    'On Post': ['On Post'],
  }
  return map[statusLabel] || []
}

export const DepartmentOpsBoard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { departmentTasks, teamMembers, resourceStatus } = useSelector((state: RootState) => state.operations)
  const [activeDept, setActiveDept] = useState('maintenance')
  const [statusFilter, setStatusFilter] = useState('All')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [expandedTask, setExpandedTask] = useState<string | number | null>(null)

  useEffect(() => {
    dispatch(fetchDepartmentTasks(activeDept))
    dispatch(fetchTeamMembers())
    dispatch(fetchResourceStatus())
  }, [dispatch, activeDept])

  const filteredTasks = departmentTasks?.filter((task) => {
    if (statusFilter !== 'All') {
      const allowed = statusLabelToFilter(statusFilter)
      if (allowed.length > 0 && !allowed.includes(task.statusLabel)) return false
      if (allowed.length === 0 && task.statusLabel !== statusFilter) return false
    }
    if (sourceFilter !== 'all' && task.sourceBadge !== sourceFilter) return false
    return true
  })

  const sourceBreakdown = departmentTasks?.reduce<Record<string, number>>((acc, t) => {
    acc[t.sourceBadge] = (acc[t.sourceBadge] || 0) + 1
    return acc
  }, {})

  return (
    <>
      <NavBar />

      <OpsPageWrapper>
        <h1 style={{ fontSize: '30px', fontWeight: 900, color: opsColors.textPrimary, margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
          Department Operations
        </h1>

        <OperationsTabBar />

        {/* Department Selector */}
        <DeptSelector>
          {DEPARTMENTS.map((dept) => (
            <DeptButton
              key={dept.key}
              active={activeDept === dept.key}
              accentColor={dept.color}
              onClick={() => setActiveDept(dept.key)}
            >
              <span>{dept.icon}</span>
              {dept.name}
            </DeptButton>
          ))}
        </DeptSelector>

        {/* Filters */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FilterPills>
            {STATUS_FILTERS.map((s) => (
              <FilterPill key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>{s}</FilterPill>
            ))}
          </FilterPills>
          <FilterPills>
            {SOURCE_FILTERS.map((s) => (
              <FilterPill key={s.value} active={sourceFilter === s.value} onClick={() => setSourceFilter(s.value)}>{s.label}</FilterPill>
            ))}
          </FilterPills>
        </div>

        {/* Main content */}
        <OpsGrid columns="65fr 35fr" gap="1.25rem">
          {/* Task Queue */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {filteredTasks?.map((task) => (
              <TaskCard key={task.id} borderColor={PRIORITY_COLORS[task.priority]}>
                <TaskCardHeader>
                  <div>
                    <TaskCardTitle>{task.name}</TaskCardTitle>
                    {task.subtitle && <TaskCardSubtitle>{task.subtitle}</TaskCardSubtitle>}
                  </div>
                  <TaskCardBadges>
                    <SourceBadge source={task.sourceBadge} />
                    <PriorityBadge priority={task.priority} />
                    <Badge bg={task.status === TaskStatus.Stopped ? opsColors.dangerBase : task.status === TaskStatus.InProgress ? opsColors.successBase : opsColors.brand500}>
                      {task.statusLabel}
                    </Badge>
                  </TaskCardBadges>
                </TaskCardHeader>
                <TaskCardMeta>
                  {task.assignee ? (
                    <span>{task.assignee}</span>
                  ) : (
                    <WarningText>Unassigned</WarningText>
                  )}
                  {task.eta && <span>ETA: {task.eta}</span>}
                  {task.impactText && <DangerText>{task.impactText}</DangerText>}
                  {task.partsMissing && <WarningText>Parts missing</WarningText>}
                </TaskCardMeta>

                {/* Expand/collapse */}
                <div
                  style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }}
                  onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                >
                  {expandedTask === task.id ? 'Hide actions' : 'Show actions'}
                </div>
                {expandedTask === task.id && (
                  <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.5rem' }}>
                    {!task.assignee && <OpsButton size="sm">Assign</OpsButton>}
                    {task.status === TaskStatus.NotStarted && <OpsButton variant="success" size="sm">Start</OpsButton>}
                    {task.status === TaskStatus.InProgress && <OpsButton variant="success" size="sm">Complete</OpsButton>}
                    <OpsButton variant="danger" size="sm">Escalate</OpsButton>
                  </div>
                )}
              </TaskCard>
            ))}
            {filteredTasks?.length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: opsColors.textFaded }}>No tasks match the current filters.</div>
            )}
          </div>

          {/* Right Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <TeamPanel members={teamMembers} />

            {/* Source Breakdown */}
            <OpsCard>
              <OpsCardHeader><h3>Source Breakdown</h3></OpsCardHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {sourceBreakdown && Object.entries(sourceBreakdown).map(([source, count]) => (
                  <div key={source} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <SourceBadge source={source as TaskSource} />
                    <span style={{ fontSize: '14px', fontWeight: 700, color: opsColors.textPrimary }}>{count}</span>
                  </div>
                ))}
              </div>
            </OpsCard>

            {/* Department-specific panels */}
            {activeDept === 'maintenance' && (
              <>
                <OpsCard>
                  <OpsCardHeader><h3>Machines Down</h3></OpsCardHeader>
                  {MOCK_MACHINES_DOWN.map((m) => (
                    <div key={m.id} style={{ padding: '0.5rem 0', borderBottom: `1px solid ${opsColors.dividerFaded}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: opsColors.textPrimary }}>{m.machineName} ({m.machineId})</div>
                      <div style={{ fontSize: '12px', color: opsColors.textFaded }}>Down since: {new Date(m.downSince).toLocaleTimeString()}</div>
                      <div style={{ fontSize: '12px', color: opsColors.dangerBase }}>{m.reason}</div>
                      {m.impactedPlan && <div style={{ fontSize: '11px', color: opsColors.warningBase }}>Impacts: {m.impactedPlan}</div>}
                    </div>
                  ))}
                </OpsCard>
                <OpsCard>
                  <OpsCardHeader><h3>Parts at Risk</h3></OpsCardHeader>
                  {MOCK_PARTS_AT_RISK.map((p) => (
                    <div key={p.id} style={{ padding: '0.5rem 0', borderBottom: `1px solid ${opsColors.dividerFaded}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: opsColors.textPrimary }}>{p.partName}</div>
                      <div style={{ fontSize: '12px', color: opsColors.textFaded }}>For: {p.forMachine}</div>
                      <div style={{ fontSize: '12px', color: opsColors.warningBase }}>{p.orderStatus}</div>
                    </div>
                  ))}
                </OpsCard>
              </>
            )}

            {activeDept === 'camp-services' && (
              <OpsCard>
                <OpsCardHeader><h3>Location Status</h3></OpsCardHeader>
                {MOCK_LOCATION_STATUS.map((loc) => (
                  <div key={loc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: `1px solid ${opsColors.dividerFaded}` }}>
                    <div>
                      <div style={{ fontSize: '14px', color: opsColors.textPrimary }}>{loc.locationName}</div>
                      {loc.assignedWorker && <div style={{ fontSize: '11px', color: opsColors.textFaded }}>{loc.assignedWorker}</div>}
                    </div>
                    <Badge bg={loc.status === 'Done' ? opsColors.successBase : loc.status === 'In Progress' ? opsColors.brand500 : opsColors.textFaded}>
                      {loc.status}
                    </Badge>
                  </div>
                ))}
              </OpsCard>
            )}

            {activeDept === 'security' && (
              <>
                <OpsCard>
                  <OpsCardHeader><h3>Post Coverage</h3></OpsCardHeader>
                  {MOCK_POST_COVERAGE.map((post) => (
                    <div key={post.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: `1px solid ${opsColors.dividerFaded}` }}>
                      <div>
                        <div style={{ fontSize: '14px', color: opsColors.textPrimary }}>{post.postName}</div>
                        <div style={{ fontSize: '11px', color: opsColors.textFaded }}>Guards: {post.guardsCount} &middot; Next: {post.nextHandover}</div>
                      </div>
                      <Badge bg={post.coverageStatus === 'Covered' ? opsColors.successBase : opsColors.dangerBase}>
                        {post.coverageStatus}
                      </Badge>
                    </div>
                  ))}
                </OpsCard>
              </>
            )}

            {activeDept === 'mining-operations' && (
              <>
                <OpsCard>
                  <OpsCardHeader><h3>Plan Progress</h3></OpsCardHeader>
                  {MOCK_PLANS.filter((p) => p.status === 'Active').map((plan) => (
                    <div key={plan.id} style={{ padding: '0.5rem 0', borderBottom: `1px solid ${opsColors.dividerFaded}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: opsColors.textPrimary }}>{plan.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '0.25rem' }}>
                        <ProgressBarWrapper style={{ flex: 1 }}>
                          <ProgressBarFill percent={plan.progress} />
                        </ProgressBarWrapper>
                        <span style={{ fontSize: '12px', color: opsColors.textSecondary }}>{plan.progress}%</span>
                      </div>
                      <div style={{ fontSize: '12px', color: opsColors.textFaded, marginTop: '0.25rem' }}>
                        {plan.currentValue}/{plan.targetValue} {plan.targetUnit}
                      </div>
                    </div>
                  ))}
                </OpsCard>
              </>
            )}
          </div>
        </OpsGrid>
      </OpsPageWrapper>
    </>
  )
}
